import type { LogLevel } from "./actions";
import type { AuditRule } from "../rules/audit/types";
import { runAudit } from "../rules/audit/engine";
import { invalidateWorkbookModel } from "../model";
import { loadAllAuditRules } from "../rules/audit/registry";
import {
  deleteUserAuditRule,
  loadAuditRuleOrder,
  loadSystemAuditRuleOverrides,
  loadUserAuditRules,
  saveAuditRuleOrder,
  saveSystemAuditRuleOverrides,
  saveUserAuditRules,
} from "../rules/audit/storage";
import { openAuditEditor } from "./dialog-host";
import { openReviewResults } from "./dialog-host";
import { loadActivePersonaId, loadPersonas } from "../rules/storage";

export interface AuditUIDeps {
  log: (message: string, level?: LogLevel) => void;
}

let deps: AuditUIDeps;
let currentRules: AuditRule[] = [];
let running = false;

export async function initAuditUI(d: AuditUIDeps): Promise<void> {
  deps = d;
  document.getElementById("audit-new")?.addEventListener("click", () => void handleNewRule());
  document.getElementById("audit-run")?.addEventListener("click", () => void handleRun());
  renderLoadingPlaceholder();
  try {
    await rerender();
  } catch (err) {
    deps.log(`audit init failed: ${errMessage(err)}`, "error");
  }
}

function renderLoadingPlaceholder(): void {
  const list = document.getElementById("audit-list");
  if (list) {
    list.replaceChildren();
    const loading = document.createElement("p");
    loading.className = "rules-empty";
    loading.textContent = "Loading…";
    list.appendChild(loading);
  }
}

async function rerender(): Promise<void> {
  try {
    currentRules = await loadAllAuditRules();
    renderList();
  } catch (err) {
    deps.log(`failed to load checks: ${errMessage(err)}`, "error");
    currentRules = [];
    renderList();
  }
}

function renderList(): void {
  const list = document.getElementById("audit-list");
  if (!list) return;
  list.replaceChildren();

  if (currentRules.length === 0) {
    const empty = document.createElement("p");
    empty.className = "rules-empty";
    empty.textContent = "No checks yet. Click “+ New check” to add one.";
    list.appendChild(empty);
    return;
  }

  for (const rule of currentRules) {
    list.appendChild(renderRow(rule));
  }
}

function renderRow(rule: AuditRule): HTMLElement {
  const item = document.createElement("div");
  item.className = "rule-item";
  item.dataset.ruleId = rule.id;
  item.setAttribute("role", "listitem");

  // No drag handle for audit rules in v1 (order tweaks aren't critical here).
  const sevDot = document.createElement("span");
  sevDot.className = `sev-dot sev-dot--${rule.severity}`;
  sevDot.title = `severity: ${rule.severity}`;
  item.appendChild(sevDot);

  const checkbox = document.createElement("input");
  checkbox.type = "checkbox";
  checkbox.checked = rule.enabled;
  checkbox.className = "rule-toggle";
  checkbox.title = rule.enabled ? "Disable check" : "Enable check";
  checkbox.setAttribute("aria-label", `Enable ${rule.name}`);
  checkbox.addEventListener("change", () => void handleToggle(rule, checkbox.checked));
  item.appendChild(checkbox);

  const body = document.createElement("div");
  body.className = "rule-body";
  const name = document.createElement("div");
  name.className = "rule-name";
  name.textContent = rule.name;
  body.appendChild(name);
  if (rule.description) {
    const desc = document.createElement("div");
    desc.className = "rule-desc";
    desc.textContent = rule.description;
    body.appendChild(desc);
  }
  item.appendChild(body);

  const badge = document.createElement("span");
  badge.className = `rule-source rule-source--${rule.source}`;
  badge.textContent = rule.source;
  item.appendChild(badge);

  const editBtn = document.createElement("button");
  editBtn.type = "button";
  editBtn.className = "rule-action-btn";
  editBtn.textContent = rule.source === "system" ? "View" : "Edit";
  editBtn.addEventListener("click", () => void handleEdit(rule));
  item.appendChild(editBtn);

  if (rule.source === "user") {
    const deleteBtn = document.createElement("button");
    deleteBtn.type = "button";
    deleteBtn.className = "rule-action-btn rule-action-btn--danger";
    deleteBtn.textContent = "Delete";
    deleteBtn.addEventListener("click", () => void handleDelete(rule));
    item.appendChild(deleteBtn);
  }

  return item;
}

async function handleToggle(rule: AuditRule, enabled: boolean): Promise<void> {
  try {
    if (rule.source === "system") {
      const overrides = await loadSystemAuditRuleOverrides();
      overrides[rule.id] = { ...overrides[rule.id], enabled };
      await saveSystemAuditRuleOverrides(overrides);
    } else {
      const userRules = await loadUserAuditRules();
      const idx = userRules.findIndex((r) => r.id === rule.id);
      if (idx === -1) {
        await rerender();
        return;
      }
      userRules[idx] = { ...userRules[idx], enabled };
      await saveUserAuditRules(userRules);
    }
    deps.log(`${rule.name}: ${enabled ? "enabled" : "disabled"}`);
    await rerender();
  } catch (err) {
    deps.log(`toggle failed: ${errMessage(err)}`, "error");
    await rerender();
  }
}

async function handleEdit(rule: AuditRule): Promise<void> {
  deps.log(`opening editor for "${rule.name}"…`);
  try {
    const saved = await openAuditEditor(rule);
    if (saved) {
      deps.log(`saved "${saved.name}"`, "success");
      await rerender();
    } else {
      deps.log("edit cancelled");
    }
  } catch (err) {
    deps.log(`edit failed: ${errMessage(err)}`, "error");
  }
}

async function handleDelete(rule: AuditRule): Promise<void> {
  if (rule.source !== "user") return;
  if (!confirm(`Delete check "${rule.name}"? This cannot be undone.`)) return;
  try {
    await deleteUserAuditRule(rule.id);
    const order = (await loadAuditRuleOrder()).filter((id) => id !== rule.id);
    await saveAuditRuleOrder(order);
    deps.log(`deleted "${rule.name}"`, "success");
    await rerender();
  } catch (err) {
    deps.log(`delete failed: ${errMessage(err)}`, "error");
  }
}

async function handleNewRule(): Promise<void> {
  deps.log("opening check editor…");
  try {
    const saved = await openAuditEditor();
    if (saved) {
      const order = await loadAuditRuleOrder();
      if (!order.includes(saved.id)) {
        order.push(saved.id);
        await saveAuditRuleOrder(order);
      }
      deps.log(`saved new check "${saved.name}"`, "success");
      await rerender();
    } else {
      deps.log("cancelled");
    }
  } catch (err) {
    deps.log(`new check failed: ${errMessage(err)}`, "error");
  }
}

async function handleRun(): Promise<void> {
  if (running) {
    deps.log("already running", "error");
    return;
  }
  const scopeSel = document.getElementById("audit-scope") as HTMLSelectElement | null;
  const scope = (scopeSel?.value ?? "sheet") as Scope;
  const runBtn = document.getElementById("audit-run") as HTMLButtonElement | null;
  running = true;
  if (runBtn) runBtn.disabled = true;

  try {
    const allRules = await loadAllAuditRules();
    const activePersonaId = loadActivePersonaId();
    const activePersona = activePersonaId
      ? (await loadPersonas()).find((p) => p.id === activePersonaId)
      : null;
    const personaFilter = activePersona?.auditRuleIds ? new Set(activePersona.auditRuleIds) : null;
    const candidates = personaFilter ? allRules.filter((r) => personaFilter.has(r.id)) : allRules;
    const enabled = candidates.filter((r) => r.enabled);

    const personaSuffix = activePersona ? ` (persona: ${activePersona.name})` : "";
    deps.log(`running ${enabled.length} review check(s) on ${scope}${personaSuffix}`);
    if (enabled.length === 0) {
      deps.log("nothing to run");
      return;
    }
    // Invalidate the cache so each user-triggered scan reads fresh
    // workbook state (cell edits don't bump the fingerprint).
    invalidateWorkbookModel(scope);
    const t0 = performance.now();
    const findings = await runAudit(enabled, scope, (msg) => deps.log(msg));
    const dt = (performance.now() - t0).toFixed(0);

    const counts = { error: 0, warning: 0, info: 0 };
    for (const f of findings) counts[f.severity]++;
    const total = findings.length;
    deps.log(
      `${dt}ms — ${total} finding(s): ${counts.error} error, ${counts.warning} warning, ${counts.info} info`,
      total > 0 ? "info" : "success"
    );

    await openReviewResults(
      findings,
      scope === "selection" ? "selection" : scope === "workbook" ? "workbook" : "active sheet"
    );
    deps.log("review closed");
  } catch (err) {
    deps.log(`review failed: ${errMessage(err)}`, "error");
  } finally {
    running = false;
    if (runBtn) runBtn.disabled = false;
  }
}

function errMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}
